import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
// Using custom Bootstrap pagination for consistency
import { getBranches, saveBranch } from "../../components/Api/GmsBranch";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  branch_Id: 0,
  branch_NameEn: "",
  branch_NameAr: "",
  branch_Buisness_Id: null,
  branch_AllowPurchase: false,
  branch_AllowSales: false,
  branch_AllowTransferIn: false,
  branch_AllowTransferOut: false,
  branch_Active: true,
  branch_CreatedDate: new Date().toISOString(),
  branch_CreatedBy: 1,
  branch_ModifiedDate: new Date().toISOString(),
  branch_ModifiedBy: 1,
};

const emptyFilter = {
  branch_Code: "",
  branch_NameEn: "",
  branch_NameAr: "",
  branch_Buisness_Id: null,
  branch_Active: null,
};

export default function BranchPage() {
  const [branches, setBranches] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [uniqueBranchCodes, setUniqueBranchCodes] = useState([]);
  const [businessOptions, setBusinessOptions] = useState([]);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const formatDateTime = (dateTimeStr) => {
    try {
      // Extract just the date part from the ISO string
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "serialNumber",
        enableColumnFilter: false,
        cell: ({ row }) => row.index + 1,
      },
      {
        header: "Code",
        accessorKey: "branch_Code",
        enableColumnFilter: false,
      },
      {
        header: "Name (En)",
        accessorKey: "branch_NameEn",
        enableColumnFilter: false,
      },
      {
        header: "Name (Ar)",
        accessorKey: "branch_NameAr",
        enableColumnFilter: false,
      },
      {
        header: "Company",
        accessorKey: "business_NameEn",
        enableColumnFilter: false,
      },
      {
        header: "Active",
        accessorKey: "branch_Active",
        enableColumnFilter: false,
        cell: ({ value }) => (value ? "Yes" : "No"),
      },
      {
        header: "View",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleView(row.original);
            }}
            className="btn btn-link p-0"
            disabled={viewLoadingRow === row.original.branch_Id}
          >
            {viewLoadingRow === row.original.branch_Id ? (
              <ClipLoader size={20} color="black" />
            ) : (
              <Eye size={20} color="black" />
            )}
          </button>
        ),
      },
      {
        header: "Edit",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          canEdit ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.original);
              }}
              className="btn btn-link p-0"
              disabled={editLoadingRow === row.original.branch_Id}
            >
              {editLoadingRow === row.original.branch_Id ? (
                <ClipLoader size={20} color="black" />
              ) : (
                <SquarePen size={20} color="black" />
              )}
            </button>
          ) : null
        ),
      },
    ],
    [viewLoadingRow, editLoadingRow, canEdit]
  );

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadBranches();
      loadBusinesses();
    }
  }, [permissionsLoading, canView]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsBranch');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsBranch');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsBranch');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  useEffect(() => {
    // Extract unique branch codes
    const codes = [...new Set(branches.map(b => b.branch_Code))];
    setUniqueBranchCodes(codes.map(code => ({ value: code, label: code })));
  }, [branches]);



  const loadBranches = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getBranches(filterData);
      console.log('Loaded branches data:', data); // Debug log
      setBranches(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load branches: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadBusinesses = async () => {
    setBusinessLoading(true);
    try {
      const data = await getBusinesses({});
      // Handle both array and object responses
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);
      
      // Create business options for the dropdown
      const options = businessData.map(business => ({
        value: business.business_Id,
        label: business.business_NameEn || business.business_Name
      }));
      setBusinessOptions(options);
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load businesses: " + error.message, "error");
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Set default active status based on permissions
  useEffect(() => {
    if (!isEditing && canEdit && !canDelete) {
      setForm(prev => ({
        ...prev,
        branch_Active: true
      }));
    }
  }, [isEditing, canEdit, canDelete]);

  const handleFilterChange = (field, value) => {
    // If company filter changes, reset branch name filters
    if (field === 'branch_Buisness_Id') {
      setFilter({
        ...filter,
        [field]: value,
        branch_NameEn: "",
        branch_NameAr: "",
      });
    } else {
      setFilter({
        ...filter,
        [field]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.branch_NameEn || !form.branch_NameEn.trim()) {
      Swal.fire("Error", "Please enter branch name in English", "error");
      return;
    }
    
    if (!form.branch_NameAr || !form.branch_NameAr.trim()) {
      Swal.fire("Error", "Please enter branch name in Arabic", "error");
      return;
    }
    
    if (!form.branch_Buisness_Id) {
      Swal.fire("Error", "Please select a Company", "error");
      return;
    }
    
    setLoading(true);
    try {
      const formData = {
        ...form,
        branch_AllowPurchase: false,
        branch_AllowSales: false,
        branch_AllowTransferIn: false,
        branch_ModifiedDate: new Date().toISOString(),
      };
      const result = await saveBranch(formData);
      Swal.fire("Success", result.message, "success");
      // Reset form and explicitly set branch_Buisness_Id to null
      setForm({
        ...defaultForm,
        branch_Buisness_Id: null
      });
      setIsEditing(false);
      loadBranches(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save branch: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (branch) => {
    setViewLoadingRow(branch.branch_Id);
    try {
      Swal.fire({
        title: branch.branch_NameEn,
        html: `
          <div class="text-left">
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Code:</strong> ${branch.branch_Code}</p>
              <p><strong>Arabic Name:</strong> ${branch.branch_NameAr}</p>
              <p><strong>Company:</strong> ${branch.business_NameEn}</p>
              <p><strong>Status:</strong> ${branch.branch_Active ? "Active" : "Inactive"}</p>
            </div>
            
            <div class="mb-3">
              <h6 class="mb-2">Permissions</h6>
              <p><strong>Allow Purchase:</strong> ${branch.branch_AllowPurchase ? "Yes" : "No"}</p>
              <p><strong>Allow Sales:</strong> ${branch.branch_AllowSales ? "Yes" : "No"}</p>
              <p><strong>Allow Transfer In:</strong> ${branch.branch_AllowTransferIn ? "Yes" : "No"}</p>
              <p><strong>Allow Transfer Out:</strong> ${branch.branch_AllowTransferOut ? "Yes" : "No"}</p>
            </div>

            <div class="mb-3">
              <h6 class="mb-2">Timestamps</h6>
              <p><strong>Created Date:</strong> ${formatFullDateTime(branch.branch_CreatedDate)}</p>
              <p><strong>Last Modified:</strong> ${formatFullDateTime(branch.branch_ModifiedDate)}</p>
            </div>
          </div>
        `,
        icon: "info",
        width: '600px',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          content: 'custom-swal-content'
        }
      });
    } catch (error) {
      Swal.fire("Error", "Failed to view branch details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (branch) => {
    console.log('Edit branch data:', branch); // Debug log
    const businessId = branch.business_Id || branch.branch_Buisness_Id;
    console.log('Selected business ID:', businessId); // Debug log
    
    setForm({
      ...branch,
      branch_Buisness_Id: businessId,
      branch_AllowPurchase: false,
      branch_AllowSales: false,
      branch_AllowTransferIn: false,
      branch_ModifiedDate: new Date().toISOString(),
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm({
      ...defaultForm,
      branch_Buisness_Id: null
    });
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadBranches(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadBranches(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get paginated data for display
  const getPaginatedBranches = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return branches.slice(startIndex, endIndex);
  };

  // Options for searchable dropdowns
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  // Filter branch name options based on selected company
  const branchNameEnOptions = useMemo(() => {
    if (!filter.branch_Buisness_Id) {
      return branches.map((b) => ({
        value: b.branch_NameEn,
        label: b.branch_NameEn,
      }));
    }
    return branches
      .filter((b) => b.branch_Buisness_Id === filter.branch_Buisness_Id)
      .map((b) => ({
        value: b.branch_NameEn,
        label: b.branch_NameEn,
      }));
  }, [branches, filter.branch_Buisness_Id]);

  const branchNameArOptions = useMemo(() => {
    if (!filter.branch_Buisness_Id) {
      return branches.map((b) => ({
        value: b.branch_NameAr,
        label: b.branch_NameAr,
      }));
    }
    return branches
      .filter((b) => b.branch_Buisness_Id === filter.branch_Buisness_Id)
      .map((b) => ({
        value: b.branch_NameAr,
        label: b.branch_NameAr,
      }));
  }, [branches, filter.branch_Buisness_Id]);

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <ClipLoader size={50} color="#556ee6" />
                  <p className="mt-3">Loading permissions...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Branch Management</h4>

                {/* Form Section - Only show if user has edit permission */}
                {canEdit && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        {isEditing ? "Edit Branch" : "Add New Branch"}
                      </h5>
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Name (EN)</Label>
                            <Input
                              name="branch_NameEn"
                              value={form.branch_NameEn}
                              onChange={(e) => handleFormChange("branch_NameEn", e.target.value)}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Name (AR)</Label>
                            <Input
                              name="branch_NameAr"
                              value={form.branch_NameAr}
                              onChange={(e) => handleFormChange("branch_NameAr", e.target.value)}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Company</Label>
                            <Select
                              value={form.branch_Buisness_Id ? businessOptions.find(
                                option => option.value === form.branch_Buisness_Id
                              ) : null}
                              onChange={(option) =>
                                handleFormChange("branch_Buisness_Id", option ? option.value : null)
                              }
                              options={businessOptions}
                              isClearable
                              placeholder={businessLoading ? "Loading companies..." : "Select Company..."}
                              maxMenuHeight={200}
                              menuPlacement="auto"
                              isSearchable
                              isLoading={businessLoading}
                              noOptionsMessage={() => businessLoading ? "Loading..." : "No companies found"}
                              styles={selectStyles}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={3}>
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="branch_Active"
                              name="branch_Active"
                              checked={form.branch_Active}
                              onChange={(e) => handleFormChange("branch_Active", e.target.checked)}
                              disabled={!canDelete} // Only allow status change if user has delete permission
                            />
                            <label className="form-check-label" htmlFor="branch_Active">
                              {form.branch_Active ? "Active" : "Inactive"}
                            </label>
                          </div>
                        </Col>
                        {/* <Col md={3}>
                          <div className="form-check">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id="branch_AllowPurchase"
                              name="branch_AllowPurchase"
                              checked={form.branch_AllowPurchase}
                              onChange={(e) => handleFormChange("branch_AllowPurchase", e.target.checked)}
                            />
                            <Label className="form-check-label" for="branch_AllowPurchase">
                              Allow Purchase
                            </Label>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="form-check">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id="branch_AllowSales"
                              name="branch_AllowSales"
                              checked={form.branch_AllowSales}
                              onChange={(e) => handleFormChange("branch_AllowSales", e.target.checked)}
                            />
                            <Label className="form-check-label" for="branch_AllowSales">
                              Allow Sales
                            </Label>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="form-check">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id="branch_AllowTransferIn"
                              name="branch_AllowTransferIn"
                              checked={form.branch_AllowTransferIn}
                              onChange={(e) => handleFormChange("branch_AllowTransferIn", e.target.checked)}
                            />
                            <Label className="form-check-label" for="branch_AllowTransferIn">
                              Allow Transfer In
                            </Label>
                          </div>
                        </Col> */}
                      </Row>

                      <div className="d-flex gap-2">
                        <Button
                          type="submit"
                          color="success"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <ClipLoader size={20} color="white" />{" "}
                              <span className="ms-2">Saving...</span>
                            </>
                          ) : isEditing ? (
                            "Update Branch"
                          ) : (
                            "Save Branch"
                          )}
                        </Button>
                        {isEditing && (
                          <Button
                            type="button"
                            color="secondary"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </Form>
                  </CardBody>
                </Card>
                )}

                {/* Filter Section */}
                <Card className="mb-4">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">Filter Branches</h5>
                      <Button
                        color="primary"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>

                    {showFilters && (
                      <>
                        <Row className="mb-3">
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Branch Code</Label>
                              <Select
                                isClearable
                                value={
                                  filter.branch_Code
                                    ? {
                                        value: filter.branch_Code,
                                        label: filter.branch_Code,
                                      }
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "branch_Code",
                                    option ? option.value : ""
                                  )
                                }
                                options={uniqueBranchCodes}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Name (EN)</Label>
                              <Select
                                isClearable
                                value={
                                  filter.branch_NameEn
                                    ? {
                                        value: filter.branch_NameEn,
                                        label: filter.branch_NameEn,
                                      }
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "branch_NameEn",
                                    option ? option.value : ""
                                  )
                                }
                                options={branchNameEnOptions}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Name (AR)</Label>
                              <Select
                                isClearable
                                value={
                                  filter.branch_NameAr
                                    ? {
                                        value: filter.branch_NameAr,
                                        label: filter.branch_NameAr,
                                      }
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "branch_NameAr",
                                    option ? option.value : ""
                                  )
                                }
                                options={branchNameArOptions}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Company</Label>
                              <Select
                                isClearable
                                value={
                                  filter.branch_Buisness_Id
                                    ? businessOptions.find(
                                        option => option.value === filter.branch_Buisness_Id
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "branch_Buisness_Id",
                                    option ? option.value : null
                                  )
                                }
                                options={businessOptions}
                                maxMenuHeight={200}
                                menuPlacement="auto"
                                placeholder={businessLoading ? "Loading companies..." : "Select Company..."}
                                isSearchable
                                isLoading={businessLoading}
                                noOptionsMessage={() => businessLoading ? "Loading..." : "No companies found"}
                                styles={selectStyles}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Status</Label>
                              <Select
                                isClearable
                                value={
                                  filter.branch_Active !== null
                                    ? statusOptions.find(
                                        (option) =>
                                          option.value === filter.branch_Active
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "branch_Active",
                                    option ? option.value : null
                                  )
                                }
                                options={statusOptions}
                              />
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button color="primary" onClick={handleApplyFilter}>
                            Apply
                          </Button>
                          <Button color="warning" onClick={handleClearFilter}>
                            Clear
                          </Button>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Table Section */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Branch List ({totalCount} records)
                    </h5>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Branch Code</th>
                            <th>Name (EN)</th>
                            <th>Name (AR)</th>
                            <th>Company</th>
                            <th>Active</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {branches.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-center">
                                No branches found
                              </td>
                            </tr>
                                                     ) : (
                             getPaginatedBranches().map((branch, index) => (
                              <tr key={branch.branch_Id}>
                                <td>{(page - 1) * pageSize + index + 1}</td>
                                <td>{branch.branch_Code}</td>
                                <td>{branch.branch_NameEn}</td>
                                <td>{branch.branch_NameAr}</td>
                                <td>{branch.business_NameEn || branch.business_name || 'N/A'}</td>
                                <td>
                                  <span className={`badge ${branch.branch_Active ? 'bg-success' : 'bg-danger'}`}>
                                    {branch.branch_Active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {canEdit && (
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => handleEdit(branch)}
                                      >
                                        Edit
                                      </Button>
                                    )}
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleView(branch)}
                                    >
                                      View
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                         style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                      <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                        <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                        Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                      </div>
                      {totalCount > pageSize && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>
                          
                          {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                            const pageNum = index + 1;
                            const isActive = page === pageNum;
                            
                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                          
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === Math.ceil(totalCount / pageSize)}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                              color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
